import type { UseFormReturn } from 'react-hook-form';
import { ShieldCheck } from 'lucide-react';
import type { SipPeerFormData } from '../schema';

/** Auth & Registration tab: registration policy + digest auth details. */
export function AuthTab({ form, profileLocked = false, profileName }: { form: UseFormReturn<SipPeerFormData>; profileLocked?: boolean; profileName?: string | null }) {
  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">Authentication &amp; Registration</h3>
        <p className="text-sm text-gray-600 mb-4">
          Configure how REGISTER and call authentication are handled for this peer.
        </p>
      </div>

      <div className="bg-gray-50 p-4 rounded-lg space-y-4">
        <h4 className="font-medium text-gray-900 flex items-center">
          <ShieldCheck className="w-5 h-5 mr-2 text-gray-600" />
          Authentication &amp; Registration
        </h4>
        {profileLocked && (
          <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
            Attached profile {profileName ? <span className="font-medium">{profileName}</span> : null} manages these legacy fields. Edit the profile instead.
          </div>
        )}

        {/* Registration Policy */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Registration Policy
          </label>
          <p className="text-xs text-gray-500 mb-2">
            Controls how SIP REGISTER requests from this peer&apos;s domain are handled.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <label className={`flex items-center p-3 border rounded-lg cursor-pointer transition-colors ${
              form.watch('registrationPolicy') === 'open'
                ? 'border-green-500 bg-green-50'
                : 'border-gray-200 hover:border-gray-300'
            }`}>
              <input
                {...form.register('registrationPolicy')}
                type="radio"
                value="open"
                className="sr-only"
                disabled={profileLocked}
              />
              <div className="flex items-center">
                <div className={`w-4 h-4 rounded-full border-2 mr-2 flex items-center justify-center ${
                  form.watch('registrationPolicy') === 'open'
                    ? 'border-green-500'
                    : 'border-gray-300'
                }`}>
                  {form.watch('registrationPolicy') === 'open' && (
                    <div className="w-2 h-2 rounded-full bg-green-500" />
                  )}
                </div>
                <div>
                  <span className="text-sm font-medium text-gray-900">Open</span>
                  <p className="text-xs text-gray-500">Allow without authentication</p>
                </div>
              </div>
            </label>

            <label className={`flex items-center p-3 border rounded-lg cursor-pointer transition-colors ${
              form.watch('registrationPolicy') === 'authenticated'
                ? 'border-blue-500 bg-blue-50'
                : 'border-gray-200 hover:border-gray-300'
            }`}>
              <input
                {...form.register('registrationPolicy')}
                type="radio"
                value="authenticated"
                className="sr-only"
                disabled={profileLocked}
              />
              <div className="flex items-center">
                <div className={`w-4 h-4 rounded-full border-2 mr-2 flex items-center justify-center ${
                  form.watch('registrationPolicy') === 'authenticated'
                    ? 'border-blue-500'
                    : 'border-gray-300'
                }`}>
                  {form.watch('registrationPolicy') === 'authenticated' && (
                    <div className="w-2 h-2 rounded-full bg-blue-500" />
                  )}
                </div>
                <div>
                  <span className="text-sm font-medium text-gray-900">Authenticated</span>
                  <p className="text-xs text-gray-500">Require digest authentication</p>
                </div>
              </div>
            </label>

            <label className={`flex items-center p-3 border rounded-lg cursor-pointer transition-colors ${
              form.watch('registrationPolicy') === 'closed'
                ? 'border-red-500 bg-red-50'
                : 'border-gray-200 hover:border-gray-300'
            }`}>
              <input
                {...form.register('registrationPolicy')}
                type="radio"
                value="closed"
                className="sr-only"
                disabled={profileLocked}
              />
              <div className="flex items-center">
                <div className={`w-4 h-4 rounded-full border-2 mr-2 flex items-center justify-center ${
                  form.watch('registrationPolicy') === 'closed'
                    ? 'border-red-500'
                    : 'border-gray-300'
                }`}>
                  {form.watch('registrationPolicy') === 'closed' && (
                    <div className="w-2 h-2 rounded-full bg-red-500" />
                  )}
                </div>
                <div>
                  <span className="text-sm font-medium text-gray-900">Closed</span>
                  <p className="text-xs text-gray-500">Reject all registrations</p>
                </div>
              </div>
            </label>
          </div>
        </div>

        {/* Authentication details - only shown when policy is 'authenticated' */}
        {form.watch('registrationPolicy') === 'authenticated' && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t border-gray-200 mt-4">
              <div className="flex items-center">
                <input
                  {...form.register('authRequired')}
                  type="checkbox"
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  disabled={profileLocked}
                />
                <label className="ml-2 block text-sm text-gray-700">
                  Also require authentication for calls (INVITE)
                </label>
              </div>

              <div className="flex items-center">
                <input
                  {...form.register('registrationRequired')}
                  type="checkbox"
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  disabled={profileLocked}
                />
                <label className="ml-2 block text-sm text-gray-700">
                  Peer must register before making calls
                </label>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Username <span className="text-gray-400 font-normal">(optional)</span>
                </label>
                <input
                  {...form.register('username')}
                  type="text"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="For outbound auth to this peer"
                  disabled={profileLocked}
                />
                <p className="text-xs text-gray-500 mt-1">Used when SBC authenticates to this peer</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Realm <span className="text-gray-400 font-normal">(optional)</span>
                </label>
                <input
                  {...form.register('realm')}
                  type="text"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder={`Defaults to: ${form.watch('domain') || 'domain'}`}
                  disabled={profileLocked}
                />
                <p className="text-xs text-gray-500 mt-1">Used in WWW-Authenticate challenge. Defaults to domain if empty.</p>
              </div>
              {form.watch('registrationRequired') && (
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Registration URI
                  </label>
                  <input
                    {...form.register('registrationUri')}
                    type="text"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                    placeholder="sip:user@domain.com"
                    disabled={profileLocked}
                  />
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
